import React, {useContext, useEffect} from "react";
import {useParams} from "react-router-dom";
import {useAuthInfo} from "@propelauth/react";
import {FormProvider, SubmitHandler, useForm} from "react-hook-form";

import {VEDetailItem, VEListItem, VEPricing, VESuggestion} from "../ve_model";
import {Card, CardContent, CardHeader, CardTitle} from "../../ui/card";
import {Button} from "../../ui/button";
import {Separator} from "../../ui/separator";
import {useToast} from "../../ui/use-toast";
import {TrashIcon} from "@radix-ui/react-icons";

import {deleteWithToken, fetchWithToken, postWithToken, putWithToken} from "../../../api/apiBase";
import {UserContext} from "../../../contexts/UserContext";
import {ProjectContext} from "../../../contexts/ProjectContext";
import {PriceForm, SuggestedByLine, SuggestionForm} from "../veItemViewParts";


function defaultPrice() {
    return {savings_amount: 0, description: " "};
}

function defaultSuggestion() {
    return {
        created_by: {name: '', email: ''},
        description: " ",
        title: " ",
        shared_state: {share_requested: false, share_enabled: false},
    };
}

function defaultVEListItem() {
    return {
        suggestion: defaultSuggestion(),
        price: defaultPrice(),
    };
}


type BuilderVeItemProps = {
    suggestion_id: string | null;
    price_id: string | null;
    setItemId: (x: string) => void;
    setSuggestionId: (x: string) => void;
    updateItem: (item: VEListItem) => void;
    insertItem: (item: VEListItem) => void;
    hideItem: (item: VEListItem) => void;
    suggestionPrice: VEListItem | null;
};


/**
 * Builder VE Item Form
 *
 * States:
 *   - Create new suggestion+price (suggestion_id=null):
 *       Shows PriceForm with title. POST creates both suggestion and price.
 *
 *   - Price an existing suggestion (suggestion_id set, price_id=null):
 *       Shows suggestion read-only + PriceForm editable.
 *       POST creates price on existing suggestion.
 *
 *   - Edit own price (suggestion_id + price_id set):
 *       Shows suggestion read-only + PriceForm editable.
 *       PUT updates the price.
 *
 * Builder never edits the suggestion itself.
 */
export function BuilderVeItemForm({
    suggestion_id,
    price_id,
    suggestionPrice,
    updateItem,
    insertItem,
    hideItem,
}: BuilderVeItemProps) {
    const {project_id} = useParams<{ project_id: string }>();
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();
    const projectContext = useContext(ProjectContext);
    const userContext = useContext(UserContext);
    const [isDisabled, setIsDisabled] = React.useState<boolean>(false);

    const isNew = suggestion_id === null;
    const isMine = suggestionPrice?.suggestion.created_by?.company.id === userContext?.company_id;
    const hasPrice = price_id !== null;

    const methods = useForm<VEListItem>();

    // --- Load form data ---
    useEffect(() => {
        methods.reset(defaultVEListItem());

        if (suggestionPrice) {
            methods.reset(suggestionPrice);
        } else if (accessToken && suggestion_id) {
            fetchWithToken(`/projects/${project_id}/ve/${suggestion_id}`, accessToken as string)
                .then((response) => {
                    methods.reset({
                        suggestion: {...response.suggestion},
                        price: response.prices.length > 0 ? response.prices[0] : defaultPrice(),
                    });
                })
                .catch((err) => {
                    toast({variant: "destructive", title: "Error", description: err || "Failed to load"});
                });
        }
    }, [suggestion_id, price_id, project_id]);

    // --- Create new suggestion + price ---
    const onCreateSuggestion = async (data: VEListItem) => {
        setIsDisabled(true);
        const transformed = {
            ...data.price,
            price: data.price,
            scope_id: data.suggestion.scope_id,
        };
        postWithToken(
            `/projects/${project_id}/ve/create`,
            JSON.stringify(transformed),
            accessToken as string
        )
            .then((response: VEDetailItem) => {
                const new_data = {
                    suggestion: response.suggestion,
                    price: response.prices.length > 0 ? response.prices[0] : null,
                };
                methods.reset(defaultVEListItem());
                insertItem(new_data);
                toast({title: "Success!", description: "Suggestion submitted.", variant: "default"});
            })
            .catch((err) => {
                toast({variant: "destructive", title: "Error", description: JSON.stringify(err) || "Could not create suggestion"});
            })
            .finally(() => setIsDisabled(false));
    };

    // --- Create price on existing suggestion ---
    const onCreatePrice = async (data: VEListItem) => {
        if (!(accessToken && suggestion_id)) return;
        setIsDisabled(true);
        postWithToken(
            `/projects/${project_id}/ve/${suggestion_id}/price`,
            JSON.stringify(data.price),
            accessToken as string
        )
            .then((response: VEPricing) => {
                methods.reset({suggestion: data.suggestion, price: response});
                updateItem({suggestion: data.suggestion, price: response});
                toast({title: "Success!", description: "Price submitted.", variant: "default"});
            })
            .catch((err) => {
                toast({variant: "destructive", title: "Error", description: err || "Could not create price"});
            })
            .finally(() => setIsDisabled(false));
    };

    // --- Update existing price ---
    const onUpdatePrice = async (data: VEListItem) => {
        if (!(accessToken && suggestion_id && price_id)) return;
        setIsDisabled(true);
        putWithToken(
            `/projects/${project_id}/ve/${suggestion_id}/price/${price_id}`,
            JSON.stringify(data.price),
            accessToken as string
        )
            .then((response: VEPricing) => {
                methods.reset({
                    suggestion: suggestionPrice ? suggestionPrice.suggestion : data.suggestion,
                    price: response,
                });
                updateItem({
                    suggestion: suggestionPrice ? suggestionPrice.suggestion : data.suggestion,
                    price: response,
                });
                toast({title: "Success!", description: "Price updated.", variant: "default"});
            })
            .catch((err) => {
                toast({variant: "destructive", title: "Error", description: err || "Could not update price"});
            })
            .finally(() => setIsDisabled(false));
    };

    // --- Delete own price ---
    const onDeletePrice = async () => {
        if (!(accessToken && suggestion_id && price_id)) return;
        deleteWithToken(
            `/projects/${project_id}/ve/${suggestion_id}/price/${price_id}`,
            accessToken
        )
            .then(() => {
                toast({title: "Deleted", description: "Price removed.", variant: "default"});
            })
            .catch((err) => {
                toast({variant: "destructive", title: "Error", description: err || "Could not delete price"});
            });
    };

    // --- Submit handler ---
    const onSubmit: SubmitHandler<VEListItem> = async (data) => {
        if (isNew) {
            await onCreateSuggestion(data);
        } else if (!hasPrice) {
            await onCreatePrice(data);
        } else {
            await onUpdatePrice(data);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <div className="flex justify-between">
                        <div>{isNew ? "New VE Suggestion" : "VE Price"}</div>
                        {isMine && hasPrice && (
                            <Button variant="outline" onClick={onDeletePrice}>
                                <TrashIcon/>
                            </Button>
                        )}
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <Separator className="m-3"/>

                        {/* Suggestion — read-only, shown when viewing someone else's suggestion */}
                        {!isMine && suggestion_id && (
                            <SuggestionForm
                                entity={suggestionPrice}
                                disabled={true}
                                hidden={false}
                            />
                        )}

                        {/* "Suggested by" line */}
                        {suggestionPrice && (
                            <>
                                <SuggestedByLine entity={suggestionPrice}/>
                                <Separator className="m-3"/>
                            </>
                        )}

                        {/* Price form — always editable for builder */}
                        <PriceForm
                            entity={suggestionPrice}
                            show_title={isNew || isMine}
                            disabled={false}
                            hidden={false}
                        />

                        <br/>
                        <Button type="submit" disabled={isDisabled}>Save</Button>
                    </form>
                </FormProvider>
            </CardContent>
        </Card>
    );
}
